// Kadrlar oqimi (employee turnover) — `xsib-hr` bilan kontrakt.
//
//   GET /api/hr/reports/turnover?from&to   → TurnoverReport
//
// Huquq: `hr.person.read` — YANGI kod ochilmagan va sabab backendda
// yozilgan (`TurnoverServiceImpl` javadoc'i): hisobot xodimlar
// RO'YXATIDAN kelib chiqadi va undan KAM ma'lumot beradi.
//
// =====================================================================
// ⚠️ 1-QOIDA — `to` KIRITILMAYDI
// =====================================================================
// Oraliq yarim ochiq: `from` kiradi, `to` KIRMAYDI. Sahifa oyni
// tanlaganda `to` sifatida KEYINGI oyning birinchi kunini yuboradi.
// "Oyning oxirgi kuni" ni yuborish 30/31 kunli oylar va kabisa
// fevralida bir kunni JIMGINA tashlab ketardi.
//
// =====================================================================
// ⚠️ 2-QOIDA — `turnoverPercent` `null` BO'LISHI MUMKIN
// =====================================================================
// Maxraj (o'rtacha shtat) nol bo'lsa backend `null` qaytaradi, `0`
// EMAS. Panel uni "—" deb ko'rsatishi SHART: "0%" degan son
// "oqim yo'q, hammasi joyida" degan MA'NOLI javob bo'lardi, holbuki
// savolning o'zi qo'yilmagan.
//
// ⚠️ `?? 0` KABI zaxira qiymat hech qayerda YOZILMAYDI.
//
// =====================================================================
// ⛔ 2a-QOIDA — SIMDA `null` UMUMAN KELMAYDI, MAYDON YO'QOLADI
// =====================================================================
// Bu fayl 2026-08-15 gacha `.nullable()` yozgan va sahifa SHU SABABDAN
// UMUMAN OCHILMASDI (`NUQSONLAR-2026-08-15.md` N1).
//
// Sabab uch tomonlama va har biri alohida "to'g'ri" edi:
//
//   `xsib-hr/application.yml`  → `default-property-inclusion: non_null`
//   `TurnoverReport.Row`       → `Double turnoverPercent` = `null`
//   bu sxema                   → `z.number().nullable()`
//
// Jackson `non_null` bilan `null` maydonni JSON'ga UMUMAN YOZMAYDI —
// ya'ni panelga `null` emas, MAYDONNING O'ZI YO'Q holat keladi. Zod'da
// `.nullable()` esa `null` ni qabul qiladi va `undefined` ni RAD ETADI,
// natijada butun javob `ZodError` bo'lardi.
//
// 2026-08-15, jonli `https://api.x-sib.uz` o'lchovi — javobda AYNAN
// shu maydonlar yo'q edi:
//   rows[0] → `turnoverPercent` YO'Q
//   total   → `nodeId`, `nodeCode`, `nodeName` YO'Q
// O'sha o'lchovda 16 ta yo'ldan birortasida ham ochiq `null` UCHRAMADI.
//
// ⚠️ Shuning uchun panelning butun qolgan qismi (693 ta maydon) ikkala
// shaklni ham qabul qiladigan `.nullish()` yozadi va natijani
// `?? null` bilan BITTA shaklga keltiradi. Bu fayl o'sha qoidadan
// chetga chiqqan yagona fayl edi — endi chiqmaydi.
//
// Qorovul: `tests/null-contract.mjs` — `.nullable()` ni BUILD paytida
// taqiqlaydi va shu yerdagi `parseTurnoverReport` ni maydoni
// O'CHIRILGAN javob bilan chaqirib ko'radi.
//
// =====================================================================
// ⚠️ 3-QOIDA — YAKUN QATORLARDAN QO'SHILMAYDI
// =====================================================================
// `total` javobda ALOHIDA keladi va u qatorlar yig'indisi emas: 100
// kishilik filialda 1 ta ketish (1%) va 2 kishilikda 1 ta ketish (50%)
// — foizlarning o'rtachasi 25.5%, haqiqiy yakun esa 1.96%.
//
// ⚠️ `truncated = true` bo'lganda qatorlar KESILGAN, `total` esa TO'LIQ
// qoladi. Panel bu holatni ochiq aytadi — "jadval to'liq" degan
// taassurot qolmasin.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

// ⚠️ `.nullish()` — `null` HAM, maydonning umuman yo'qligi HAM qabul
//    qilinadi (2a-qoida). `.nullable()` YOZMANG: u `undefined` ni rad
//    etadi va `non_null` ostida javob AYNAN shu ko'rinishda keladi.
const turnoverRowSchema = z.object({
  /** `null` — tayinlash davri yo'q xodimlar (yashirilmaydi). */
  nodeId: uuidSchema.nullish(),
  nodeCode: z.string().nullish(),
  nodeName: z.string().nullish(),
  headcountStart: z.number(),
  hired: z.number(),
  terminated: z.number(),
  headcountEnd: z.number(),
  averageHeadcount: z.number(),
  /** ⚠️ `null` = maxraj nol. "0%" ga AYLANTIRILMAYDI (2-qoida). */
  turnoverPercent: z.number().nullish(),
})

const turnoverReportSchema = z.object({
  from: z.string(),
  toExclusive: z.string(),
  rows: z.array(turnoverRowSchema),
  total: turnoverRowSchema,
  truncated: z.boolean(),
})

/**
 * ⚠️ Ilova ichida `undefined` YO'Q — faqat `null`.
 *
 * Sxema ikkala shaklni ham qabul qiladi, lekin ekran kodi bitta shakl
 * bilan ishlashi kerak: `HrTurnoverPage` da `row.nodeId === null`
 * yozilgan va `undefined` o'sha tekshiruvdan JIMGINA o'tib ketardi —
 * "tayinlanmagan" qatori bo'sh nom bilan chizilardi, xatosiz.
 *
 * Bu panelning umumiy naqshi: 371 ta ixtiyoriy maydon nomidan 312 tasi
 * aynan shunday `?? null` bilan normallashtiriladi
 * (`financeApi.buildAccountTree` dagi `parentId` bilan bir xil qaror).
 */
export interface TurnoverRow {
  nodeId: string | null
  nodeCode: string | null
  nodeName: string | null
  headcountStart: number
  hired: number
  terminated: number
  headcountEnd: number
  averageHeadcount: number
  turnoverPercent: number | null
}

export interface TurnoverReport {
  from: string
  toExclusive: string
  rows: TurnoverRow[]
  total: TurnoverRow
  truncated: boolean
}

function toRow(raw: z.infer<typeof turnoverRowSchema>): TurnoverRow {
  return {
    nodeId: raw.nodeId ?? null,
    nodeCode: raw.nodeCode ?? null,
    nodeName: raw.nodeName ?? null,
    headcountStart: raw.headcountStart,
    hired: raw.hired,
    terminated: raw.terminated,
    headcountEnd: raw.headcountEnd,
    averageHeadcount: raw.averageHeadcount,
    turnoverPercent: raw.turnoverPercent ?? null,
  }
}

/**
 * Xom javobni tekshiradi.
 *
 * ⚠️ ALOHIDA EKSPORT — `fetchTurnover` ichida qolib ketmasin.
 * `tests/hr-turnover.mjs` aynan shu funksiyani chaqiradi, ya'ni sinov
 * sxemaning NUSXASI ustida emas, jonli kod ustida yuradi
 * (`tests/hr.mjs` dagi `parsePerson` bilan bir xil qaror).
 */
export function parseTurnoverReport(raw: unknown): TurnoverReport {
  const parsed = turnoverReportSchema.parse(raw)
  return {
    from: parsed.from,
    toExclusive: parsed.toExclusive,
    rows: parsed.rows.map(toRow),
    total: toRow(parsed.total),
    truncated: parsed.truncated,
  }
}

export async function fetchTurnover(
  params: { from: string; to: string },
  signal?: AbortSignal,
): Promise<TurnoverReport> {
  // ⚠️ `signal` SHARTLI qo'shiladi — `undefined` ni ochiq uzatish
  //    `exactOptionalPropertyTypes` ostida tip xatosi beradi
  //    (`fetchBranchReport` da aynan shu naqsh).
  const response = await api.get<unknown>('/api/hr/reports/turnover', {
    ...(signal ? { signal } : {}),
    params: { from: params.from, to: params.to },
  })
  return parseTurnoverReport(response.data)
}
